import { useEffect, useMemo, useState } from 'react'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { Download, ExternalLink, Info, User } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import {
  BulkActionBar,
  Button,
  Checkbox,
  Chip,
  DataTable,
  DateRangePicker,
  EmptyState,
  ExportDrawer,
  MasterDetail,
  PageHeaderBand,
  SearchSelect,
  Tooltip,
  ViewToggle,
  type ExportMetadataEntry,
  type MasterDetailItem,
} from '@/components/ui'
import {
  useCustomerProductPricing,
  useCustomerSearchForPricing,
  useProductPricingForCustomer,
  useProductSearchForPricing,
  useSalespeopleSearch,
  type PricingFilters,
  type PricingSort,
} from '../api/queries'
import type {
  CustomerPricingRow,
  CustomerSearchRow,
  LookupDirection,
  PricingRow,
  ProductSearchRow,
} from '../api/types'
import { CompareDrawer } from '../components/CompareDrawer'
import { CostProvenanceHint } from '../components/CostProvenanceHint'
import { CustomerOrderHistoryDrawer } from '../components/CustomerOrderHistoryDrawer'
import { PricingFilterRail } from '../components/PricingFilterRail'
import { CostTrendChip, MarginCell, TrendDeltaChip, isCustomerRow, pricingRowId } from '../components/PricingRowHelpers'
import { Sparkline } from '../components/Sparkline'
import { pricingExportColumns } from '../utils/csv'

interface ListRow extends MasterDetailItem {
  entityId: number
}

type Unit = 'kg' | 'lb'

/** Compare drawer caps at 4 entities — beyond that a side-by-side grid
 *  stops being scannable and starts needing its own horizontal scroll,
 *  which the drawer's fixed `xl` width is deliberately sized to avoid. */
const MAX_COMPARE_SELECTION = 4

const SORT_OPTIONS: { value: PricingSort; label: string }[] = [
  { value: 'margin_pct', label: 'Worst margin' },
  { value: 'last_order_date', label: 'Most recent' },
  { value: 'price_per_kg', label: 'Highest $/kg' },
]

const DIRECTION_OPTIONS: { value: LookupDirection; label: string }[] = [
  { value: 'by-customer', label: 'By customer' },
  { value: 'by-product', label: 'By product' },
]

const DEFAULT_MARGIN_FLOOR_PCT = 15

/** Backend default lookback is 730 days when date_from/date_to are
 *  omitted — the picker's initial value mirrors that explicitly instead
 *  of silently relying on the server default, so the UI never shows a
 *  date range that doesn't match what's actually being queried. None of
 *  DateRangePicker's built-in presets (MTD/QTD/YTD/12mo) land on exactly
 *  730 days, so this renders as an unpressed "custom" range on load —
 *  correct, since 730 days isn't one of those four presets. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function daysAgoIso(days: number, today = new Date()): string {
  const d = new Date(today)
  d.setDate(d.getDate() - days)
  return isoDate(d)
}

export function ProductCustomerPricingPage() {
  const [direction, setDirection] = useState<LookupDirection>('by-customer')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [unit, setUnit] = useState<Unit>('kg')
  const [sort, setSort] = useState<PricingSort>('margin_pct')
  const [pricelistOnly, setPricelistOnly] = useState(false)
  const [selectedCustomerRow, setSelectedCustomerRow] = useState<CustomerPricingRow | null>(null)
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [compareRows, setCompareRows] = useState<PricingRow[]>([])

  const [dateFrom, setDateFrom] = useState(() => daysAgoIso(730))
  const [dateTo, setDateTo] = useState(() => isoDate(new Date()))
  const [marginFloorPct, setMarginFloorPct] = useState(DEFAULT_MARGIN_FLOOR_PCT)
  const [salespersonInput, setSalespersonInput] = useState('')
  const [debouncedSalesperson, setDebouncedSalesperson] = useState('')
  const [vendorNameInput, setVendorNameInput] = useState('')
  const [debouncedVendorName, setDebouncedVendorName] = useState('')
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(true)
  const [exportOpen, setExportOpen] = useState(false)

  // Distinct from `salespersonInput` above (PricingFilterRail's row-level
  // substring filter on the invoiced salesperson) — this one narrows WHICH
  // customers appear in the "By customer" master list, to that rep's
  // assigned book (res.partner.user_id/user_id_2), not which pricing rows
  // show once a customer is already selected.
  const [salespersonFilterQuery, setSalespersonFilterQuery] = useState('')
  const [debouncedSalespersonFilterQuery, setDebouncedSalespersonFilterQuery] = useState('')
  const [salespersonFilterId, setSalespersonFilterId] = useState<number | null>(null)
  const [salespersonFilterLabel, setSalespersonFilterLabel] = useState<string | null>(null)

  useEffect(() => {
    // 350ms, not the usual 200ms — this debounce feeds the shared Odoo
    // partner/product search endpoint, which briefly 429s under bursts
    // of rapid search-as-you-type calls (seen live). Fewer, further-
    // apart requests while typing is the cheapest way to avoid tripping
    // that limit in the first place (see SEARCH_RETRY in api/queries.ts
    // for the complementary fix: ride out a blip if one still happens).
    const id = setTimeout(() => setDebounced(query), 350)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSalesperson(salespersonInput), 200)
    return () => clearTimeout(id)
  }, [salespersonInput])

  useEffect(() => {
    const id = setTimeout(() => setDebouncedVendorName(vendorNameInput), 200)
    return () => clearTimeout(id)
  }, [vendorNameInput])

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSalespersonFilterQuery(salespersonFilterQuery), 200)
    return () => clearTimeout(id)
  }, [salespersonFilterQuery])

  // Toggling direction only clears the selected entity + any open
  // drawer — a selected product's id means nothing once the list
  // becomes customers, and vice versa. Everything else (unit, sort,
  // date range, filters, and even the in-flight search text) carries
  // over unchanged.
  const handleDirectionChange = (next: LookupDirection) => {
    setDirection(next)
    setSelectedId(undefined)
    setSelectedCustomerRow(null)
    // A customer selection means nothing once the table becomes products,
    // and vice versa — clear compare state along with the rest of the
    // direction-scoped selection.
    setSelection({})
    setCompareRows([])
  }

  // Selection itself is uncapped — it now backs both Compare (2-4 rows)
  // AND Export (any number, including "select all"). MAX_COMPARE_SELECTION
  // is enforced only where Compare is actually invoked (the Compare button
  // below disables itself past the cap instead of the checkboxes refusing
  // to check).
  const handleSelectionChange = (next: RowSelectionState) => {
    setSelection(next)
  }

  const pricingFilters = useMemo<PricingFilters>(
    () => ({
      dateFrom,
      dateTo,
      marginFloorPct,
      salesperson: debouncedSalesperson,
      vendorName: debouncedVendorName,
    }),
    [dateFrom, dateTo, marginFloorPct, debouncedSalesperson, debouncedVendorName],
  )

  const {
    data: productSearchData,
    isLoading: isSearchingProducts,
    isError: isProductSearchError,
  } = useProductSearchForPricing(debounced, direction === 'by-product')
  const {
    data: customerSearchData,
    isLoading: isSearchingCustomers,
    isError: isCustomerSearchError,
  } = useCustomerSearchForPricing(debounced, direction === 'by-customer', salespersonFilterId ?? undefined)
  const { data: salespeopleData, isLoading: isSalespeopleLoading } = useSalespeopleSearch(
    debouncedSalespersonFilterQuery,
    direction === 'by-customer',
  )

  const products = useMemo<ProductSearchRow[]>(() => productSearchData?.products ?? [], [productSearchData])
  const customerOptions = useMemo<CustomerSearchRow[]>(() => customerSearchData?.partners ?? [], [customerSearchData])
  const salespeopleOptions = useMemo(() => salespeopleData?.salespeople ?? [], [salespeopleData])
  const salespersonItems = useMemo(
    () => salespeopleOptions.map((s) => ({ value: String(s.id), label: s.name })),
    [salespeopleOptions],
  )

  const handleSalespersonFilterSelect = (val: string) => {
    const id = Number(val)
    const match = salespeopleOptions.find((s) => s.id === id)
    setSalespersonFilterId(id)
    setSalespersonFilterLabel(match?.name ?? null)
  }

  const handleSalespersonFilterClear = () => {
    setSalespersonFilterId(null)
    setSalespersonFilterLabel(null)
    setSalespersonFilterQuery('')
  }

  const isSearching = direction === 'by-product' ? isSearchingProducts : isSearchingCustomers
  const isSearchError = direction === 'by-product' ? isProductSearchError : isCustomerSearchError
  const searchCount = direction === 'by-product' ? products.length : customerOptions.length
  const searchNoun = direction === 'by-product' ? 'product' : 'customer'

  const items = useMemo<ListRow[]>(() => {
    if (direction === 'by-product') {
      return products.map((p) => ({
        id: String(p.id),
        primary: p.name,
        secondary: p.default_code || undefined,
        entityId: p.id,
      }))
    }
    return customerOptions.map((c) => ({
      id: String(c.id),
      primary: c.name,
      entityId: c.id,
    }))
  }, [direction, products, customerOptions])

  // Derived, not effect-synced: MasterDetail's own autoSelectFirst only
  // re-fires when `items.length` changes, so toggling direction into a
  // list that happens to have the same item count (e.g. both directions
  // currently showing 2 results) would otherwise leave nothing selected
  // after handleDirectionChange clears selectedId. Computing the
  // effective id at render time (rather than setState-in-an-effect)
  // sidesteps that edge case without a cascading-render lint violation —
  // MasterDetail is told about it via `selectedId` and `autoSelectFirst`
  // is turned off here since this page now owns the "pick a default"
  // decision.
  const effectiveSelectedId = selectedId ?? items[0]?.id
  const selectedEntityId = effectiveSelectedId ? Number(effectiveSelectedId) : null

  const forwardPricing = useCustomerProductPricing(
    direction === 'by-product' ? selectedEntityId : null,
    sort,
    pricingFilters,
  )
  const reversePricing = useProductPricingForCustomer(
    direction === 'by-customer' ? selectedEntityId : null,
    sort,
    pricingFilters,
  )

  const isLoadingPricing = direction === 'by-product' ? forwardPricing.isLoading : reversePricing.isLoading
  const warnings =
    direction === 'by-product'
      ? forwardPricing.data?.data_quality.warnings ?? []
      : reversePricing.data?.data_quality.warnings ?? []

  const allRows = useMemo<PricingRow[]>(() => {
    if (direction === 'by-product') return forwardPricing.data?.customers ?? []
    return reversePricing.data?.products ?? []
  }, [direction, forwardPricing.data, reversePricing.data])

  // "Pricelist only" narrows to rows that already have a dedicated (non-
  // default) Odoo pricelist — same `has_dedicated_pricelist` flag the
  // "Dedicated pricelist" column renders, just as a filter instead of a
  // column. Applies identically in both directions since every PricingRow
  // carries the flag.
  const rows = useMemo<PricingRow[]>(
    () => (pricelistOnly ? allRows.filter((r) => r.has_dedicated_pricelist) : allRows),
    [allRows, pricelistOnly],
  )

  // Derived, not effect-synced (same rationale as `effectiveSelectedId`
  // above): selection is entity-keyed, not row-index-keyed, so a re-sort
  // or a unit toggle (neither of which changes which entities are in
  // `rows`) never touches this. Only ids that genuinely disappeared — a
  // margin-floor/salesperson/vendor filter narrowing the result set, or
  // the master-detail list moving to a different product/customer — get
  // dropped, computed fresh every render rather than reconciled via a
  // setState-in-effect (which would cause a cascading extra render for
  // every `rows` change).
  const effectiveSelection = useMemo<RowSelectionState>(() => {
    const validIds = new Set(rows.map((r) => pricingRowId(r)))
    let changed = false
    const next: RowSelectionState = {}
    for (const id of Object.keys(selection)) {
      if (!selection[id]) continue
      if (validIds.has(id)) {
        next[id] = true
      } else {
        changed = true
      }
    }
    return changed ? next : selection
  }, [selection, rows])

  const selectedCount = useMemo(
    () => Object.values(effectiveSelection).filter(Boolean).length,
    [effectiveSelection],
  )
  const selectedRows = useMemo(
    () => rows.filter((r) => effectiveSelection[pricingRowId(r)]),
    [rows, effectiveSelection],
  )

  const headerName =
    direction === 'by-product' ? forwardPricing.data?.product?.name : reversePricing.data?.customer?.name
  const headerCode = direction === 'by-product' ? forwardPricing.data?.product?.default_code : null

  const description =
    direction === 'by-product'
      ? "For one product, what each customer last paid — normalized to $/kg or $/lb — and the true margin on that sale."
      : "For one customer, what they last paid across every product they buy — normalized to $/kg or $/lb — and the true margin on that sale."

  const columns = useMemo<ColumnDef<PricingRow, unknown>[]>(
    () => {
      const cols: ColumnDef<PricingRow, unknown>[] = [
        {
          id: '__select',
          header: ({ table }) => (
            <Checkbox
              aria-label="Select all rows"
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: (ctx) => {
            const row = ctx.row
            const isSelected = row.getIsSelected()
            const name = isCustomerRow(row.original) ? row.original.customer_name : row.original.product_name
            return (
              // The row itself has an onRowClick (opens the order-history
              // drawer in by-product mode) — stop propagation so checking
              // the box doesn't also open that drawer.
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={isSelected ? `Deselect ${name ?? 'row'}` : `Select ${name ?? 'row'}`}
                  checked={isSelected}
                  onChange={row.getToggleSelectedHandler()}
                />
              </div>
            )
          },
          enableSorting: false,
        },
        {
          id: 'identity',
          header: direction === 'by-product' ? 'Customer' : 'Product',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            if (isCustomerRow(row)) return row.customer_name
            return (
              <div className="flex flex-col">
                <span>{row.product_name}</span>
                {row.default_code ? (
                  <span className="text-role-metadata text-tertiary">{row.default_code}</span>
                ) : null}
              </div>
            )
          },
        },
        {
          id: 'last_order_date',
          header: 'Last order',
          accessorKey: 'last_order_date',
          cell: (ctx) => {
            const v = ctx.getValue() as string | null
            return v ? new Date(v).toLocaleDateString() : <span className="text-tertiary">—</span>
          },
        },
        {
          id: 'qty_last',
          header: 'Qty (last order)',
          accessorKey: 'qty_last',
          cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
        },
        {
          id: 'price_per_unit',
          header: unit === 'kg' ? 'Price / kg' : 'Price / lb',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            const price = unit === 'kg' ? row.price_per_kg : row.price_per_lb
            if (price === null) {
              return (
                <span className="text-tertiary" title="No weight UoM and no product weight — can't normalize">
                  can't normalize
                </span>
              )
            }
            return price.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 })
          },
        },
        {
          id: 'cost_per_unit',
          header: unit === 'kg' ? 'Cost / kg' : 'Cost / lb',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            const cost = unit === 'kg' ? row.cost_per_kg_last : row.cost_per_lb_last
            if (cost == null) return <span className="text-tertiary">—</span>
            return (
              <span className="inline-flex items-center gap-1">
                {cost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 })}
                {row.cost_predates_first_po_last ? <CostProvenanceHint /> : null}
              </span>
            )
          },
        },
        {
          id: 'true_margin_pct_last',
          header: 'Margin % (last order)',
          accessorKey: 'true_margin_pct_last',
          cell: (ctx) => <MarginCell value={ctx.getValue() as number | null} />,
        },
        {
          id: 'margin_trend',
          header: 'Margin trend',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            const hist = row.history ?? []
            const label = isCustomerRow(row) ? row.customer_name : row.product_name
            if (hist.length < 2) {
              return <span className="text-role-metadata text-tertiary">Not enough data</span>
            }
            const points = hist.slice(-12).map((h) => ({ date: h.invoice_date ?? '', value: h.true_margin_pct ?? 0 }))
            const trend = row.margin_trend
            return (
              <div className="flex items-center gap-2">
                <Sparkline points={points} ariaLabel={`Margin trend for ${label ?? searchNoun}`} />
                {trend ? <TrendDeltaChip trend={trend} /> : null}
              </div>
            )
          },
        },
        {
          id: 'cost_trend_yoy',
          header: 'Cost Trend (YoY)',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            if (row.normalization_status === 'unavailable') {
              return <span className="text-tertiary">can't normalize</span>
            }
            const trend = row.cost_trend_yoy
            if (!trend) {
              return <span className="text-role-metadata text-tertiary">Not enough data</span>
            }
            return <CostTrendChip trend={trend} />
          },
        },
        {
          id: 'has_dedicated_pricelist',
          header: () => (
            <span className="inline-flex items-center gap-1">
              Dedicated pricelist
              <Tooltip label="This customer has a dedicated (non-default) Odoo pricelist for this product — not a statement about whether this sale was priced correctly.">
                <Info size={12} className="text-tertiary" aria-label="What does dedicated pricelist mean?" />
              </Tooltip>
            </span>
          ),
          accessorKey: 'has_dedicated_pricelist',
          cell: (ctx) => (
            <Chip size="xs" variant={ctx.getValue() ? 'success' : 'neutral'}>
              {ctx.getValue() ? 'Yes' : 'No'}
            </Chip>
          ),
        },
        {
          id: 'margin_floor',
          header: 'Margin floor',
          accessorFn: (r) => r,
          cell: (ctx) => {
            const row = ctx.getValue() as PricingRow
            if (row.true_margin_pct_last === null) return <Chip size="xs" variant="neutral">—</Chip>
            return (
              <Chip size="xs" variant={row.below_margin_floor ? 'danger' : 'success'}>
                {row.below_margin_floor ? 'Below floor' : 'At/above floor'}
              </Chip>
            )
          },
        },
      ]

      // Booked (sale-order) $ / % are forward-mode-only — the backend
      // never returns them on reverse-mode ProductPricingRow (sale
      // orders aren't grouped per-product the way invoice lines are).
      if (direction === 'by-product') {
        cols.push(
          {
            id: 'ordered_revenue',
            header: 'Booked $',
            accessorFn: (r) => r,
            cell: (ctx) => {
              const row = ctx.getValue() as PricingRow
              if (!isCustomerRow(row)) return <span className="text-tertiary">—</span>
              return row.ordered_revenue.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
            },
          },
          {
            id: 'ordered_margin_pct',
            header: 'Booked %',
            accessorFn: (r) => r,
            cell: (ctx) => {
              const row = ctx.getValue() as PricingRow
              if (!isCustomerRow(row)) return <span className="text-tertiary">—</span>
              return <MarginCell value={row.ordered_margin_pct} />
            },
          },
        )
      }

      cols.push(
        {
          id: 'orders_count_in_window',
          header: 'Orders',
          accessorKey: 'orders_count_in_window',
        },
        {
          id: 'total_revenue_in_window',
          header: 'Revenue',
          accessorKey: 'total_revenue_in_window',
          cell: (ctx) => (ctx.getValue() as number).toLocaleString(undefined, { style: 'currency', currency: 'USD' }),
        },
      )

      return cols
    },
    [direction, unit, searchNoun],
  )

  const exportColumns = useMemo(() => pricingExportColumns(direction, unit), [direction, unit])
  const exportRows = selectedRows.length ? selectedRows : rows
  const exportMetadata = useMemo<ExportMetadataEntry[]>(() => {
    const entries: ExportMetadataEntry[] = [
      { label: direction === 'by-product' ? 'Product' : 'Customer', value: headerName ?? '—' },
      { label: 'Period', value: `${new Date(dateFrom).toLocaleDateString()} – ${new Date(dateTo).toLocaleDateString()}` },
      { label: 'Unit', value: unit === 'kg' ? '$/kg' : '$/lb' },
    ]
    if (direction === 'by-customer' && salespersonFilterLabel) {
      entries.push({ label: 'Salesperson filter', value: salespersonFilterLabel })
    }
    entries.push({ label: 'Rows', value: selectedRows.length ? `${selectedRows.length} selected` : `All ${rows.length} visible` })
    return entries
  }, [direction, headerName, dateFrom, dateTo, unit, salespersonFilterLabel, selectedRows.length, rows.length])

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand title="Product Customer Pricing" description={description}>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <ViewToggle<LookupDirection>
            aria-label="Lookup direction"
            value={direction}
            onChange={handleDirectionChange}
            options={DIRECTION_OPTIONS}
          />
          <DateRangePicker
            value={{ from: dateFrom, to: dateTo }}
            onChange={(range) => {
              setDateFrom(range.from)
              setDateTo(range.to)
            }}
          />
        </div>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <PricingFilterRail
          marginFloorPct={marginFloorPct}
          onMarginFloorPctChange={setMarginFloorPct}
          salesperson={salespersonInput}
          onSalespersonChange={setSalespersonInput}
          vendorName={vendorNameInput}
          onVendorNameChange={setVendorNameInput}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
        />

        <div className="min-h-0 flex-1 overflow-hidden p-4">
          {isSearchError ? (
            <EmptyState
              variant="error"
              title={`Couldn't load ${searchNoun}s`}
              description={
                direction === 'by-product'
                  ? 'The /api/pricelists/search/products endpoint returned an error. Try again.'
                  : 'The /api/pricelists/search/partners endpoint returned an error. Try again.'
              }
            />
          ) : (
            <MasterDetail<ListRow>
              items={items}
              selectedId={effectiveSelectedId}
              onSelect={setSelectedId}
              onSearch={setQuery}
              searchPlaceholder={isSearching ? `Loading ${searchNoun}s…` : `Search ${searchCount} ${searchNoun}${searchCount === 1 ? '' : 's'}`}
              autoSelectFirst={false}
              toolbarExtra={
                direction === 'by-customer' ? (
                  <SearchSelect
                    items={salespersonItems}
                    selectedLabel={salespersonFilterLabel}
                    onSelect={handleSalespersonFilterSelect}
                    onClear={handleSalespersonFilterClear}
                    onQueryChange={setSalespersonFilterQuery}
                    placeholder="Filter by salesperson…"
                    emptyMessage={isSalespeopleLoading ? 'Loading…' : 'No salesperson matches'}
                    icon={User}
                    className="w-56"
                  />
                ) : undefined
              }
              renderDetailHeader={
                direction === 'by-customer' && selectedEntityId !== null
                  ? () => (
                      <Link
                        to="/pricelists"
                        search={{ partner: selectedEntityId }}
                        className="inline-flex items-center gap-1 text-xs text-info hover:underline"
                      >
                        View pricelist <ExternalLink size={12} />
                      </Link>
                    )
                  : undefined
              }
              renderDetail={(row) => (
                <div className="flex h-full flex-col gap-3 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 className="text-role-page-subtitle">{headerName ?? row.primary}</h2>
                      {headerCode ? <p className="text-role-metadata">{headerCode}</p> : null}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(['kg', 'lb'] as const).map((u) => (
                        <button
                          key={u}
                          type="button"
                          aria-pressed={unit === u}
                          onClick={() => setUnit(u)}
                          className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
                        >
                          $/{u}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {SORT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          aria-pressed={sort === o.value}
                          onClick={() => setSort(o.value)}
                          className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
                        >
                          {o.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        aria-pressed={pricelistOnly}
                        onClick={() => setPricelistOnly((v) => !v)}
                        className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
                      >
                        Pricelist only
                      </button>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setExportOpen(true)}
                      disabled={rows.length === 0}
                    >
                      <Download size={14} /> {selectedCount > 0 ? `Export (${selectedCount})` : 'Export…'}
                    </Button>
                  </div>

                  {warnings.length ? (
                    <div className="rounded-sm border border-warning bg-warning-bg px-3 py-2 text-sm text-warning-text">
                      {warnings.join(' ')}
                    </div>
                  ) : null}

                  <div className="min-h-0 flex-1">
                    <DataTable<PricingRow>
                      data={rows}
                      columns={columns}
                      loading={isLoadingPricing}
                      getRowId={(r) => pricingRowId(r)}
                      selection={effectiveSelection}
                      onSelectionChange={handleSelectionChange}
                      moduleId="product-customer-pricing-detail"
                      maxHeight="100%"
                      minTableWidth={1200}
                      onRowClick={
                        direction === 'by-product'
                          ? (r) => {
                              // Mutual exclusion with CompareDrawer — both are
                              // independent fixed-position overlays at the
                              // same z-drawer layer; opening one must close
                              // the other or they stack invisibly on top of
                              // each other.
                              setCompareRows([])
                              setSelectedCustomerRow(r as CustomerPricingRow)
                            }
                          : undefined
                      }
                      emptyState={
                        pricelistOnly && allRows.length > 0 ? (
                          <EmptyState
                            variant="no-data"
                            title="No rows have a dedicated pricelist"
                            description={`${allRows.length} row${allRows.length === 1 ? '' : 's'} in this window, none on a dedicated (non-default) pricelist. Turn off "Pricelist only" to see them.`}
                          />
                        ) : (
                          <EmptyState
                            variant="no-data"
                            title={`No invoiced history for this ${direction === 'by-product' ? 'product' : 'customer'}`}
                            description={`No posted customer invoices in this window.`}
                          />
                        )
                      }
                    />
                  </div>
                </div>
              )}
            />
          )}
        </div>
      </div>

      <CustomerOrderHistoryDrawer
        row={selectedCustomerRow}
        onClose={() => setSelectedCustomerRow(null)}
        unit={unit}
      />

      {/* Below 2 selected there's nothing useful to do (comparing 1 row
       *  isn't a comparison) — the bar stays unmounted entirely rather
       *  than showing with a disabled/hidden Compare button. Selection
       *  itself is uncapped (it also backs Export, above) — past
       *  MAX_COMPARE_SELECTION the Compare button disables itself with a
       *  tooltip instead of the checkboxes refusing to check. */}
      {selectedCount >= 2 ? (
        <BulkActionBar count={selectedCount} onClear={() => setSelection({})}>
          {selectedCount > MAX_COMPARE_SELECTION ? (
            <Tooltip label={`Compare supports up to ${MAX_COMPARE_SELECTION} rows — narrow your selection to compare.`}>
              <span tabIndex={0}>
                <Button variant="secondary" size="sm" disabled>
                  Compare ({selectedCount})
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Mutual exclusion with CustomerOrderHistoryDrawer — see the
                // row-click handler above for the reverse direction.
                setSelectedCustomerRow(null)
                setCompareRows(selectedRows)
              }}
            >
              Compare ({selectedCount})
            </Button>
          )}
        </BulkActionBar>
      ) : null}

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={exportRows}
        columns={exportColumns}
        moduleId="product-customer-pricing"
        filenameBase="spi-customer-pricing"
        title="Export pricing"
        metadata={exportMetadata}
      />

      <CompareDrawer
        rows={compareRows}
        direction={direction}
        unit={unit}
        onClose={() => setCompareRows([])}
        onRemove={(id) => {
          setCompareRows((prev) => prev.filter((r) => pricingRowId(r) !== id))
          setSelection((prev) => {
            if (!prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
          })
        }}
      />
    </div>
  )
}
